// Spectator remote camera (NET C4). PURE (no three/DOM): where the camera sits when
// you follow another player. A third-person chase cam rides `distance` behind the
// target along its look, at eye height plus a small lift, aimed where the target
// aims — so a spectator sees roughly what that player sees, over their shoulder.

export interface Target { x: number; y: number; z: number; yaw: number; pitch: number; }
export interface CamPose { x: number; y: number; z: number; yaw: number; pitch: number; }

export interface FollowOpts {
  /** Blocks behind the target (0 = first-person, from the target's eyes). */
  distance?: number;
  /** Target eye height above its feet. */
  eyeHeight?: number;
  /** Extra lift above the eye line so the head doesn't fill the view. */
  lift?: number;
}

/** The camera pose that follows `t` over the shoulder. The forward vector matches the
 *  game's look convention (−cosθ·sinψ, sinθ, −cosθ·cosψ for pitch θ, yaw ψ); the
 *  camera sits back along −forward and looks along the same yaw/pitch. */
export function followCamera(t: Target, opts: FollowOpts = {}): CamPose {
  const distance = opts.distance ?? 3.5;
  const eyeHeight = opts.eyeHeight ?? 1.62;
  const lift = opts.lift ?? 0.4;
  const fx = -Math.cos(t.pitch) * Math.sin(t.yaw);
  const fy = Math.sin(t.pitch);
  const fz = -Math.cos(t.pitch) * Math.cos(t.yaw);
  return {
    x: t.x - fx * distance,
    y: t.y + eyeHeight + lift - fy * distance,
    z: t.z - fz * distance,
    yaw: t.yaw,
    pitch: t.pitch,
  };
}

/** The next spectator target id, cycling through `ids` (wraps); null if none. Passing
 *  the current id advances to the following one; null starts at the first. */
export function nextSpectatorTarget(currentId: number | null, ids: readonly number[]): number | null {
  if (ids.length === 0) return null;
  const i = currentId === null ? -1 : ids.indexOf(currentId);
  return ids[(i + 1) % ids.length] ?? ids[0]!;
}
